import { Fragment } from "react";
import {
  ListNumbers,
  Check,
  X,
  NotePencil,
  CheckCircle,
  CalendarX,
  ArrowsClockwise,
  Warning,
} from "@phosphor-icons/react";
import DatePicker from "../../../ui/DatePicker";
import TimePicker from "../../../ui/TimePicker";
import ConfirmDialog from "../../../ui/ConfirmDialog";
import {
  HEBREW_DAYS_FULL,
  formatHebrewDate,
} from "../../../../utils/dateUtils";

/**
 * Series overview for the wizard's edit summary: all occurrences with inline
 * per-row edit (date/time/type, admin therapist reassignment), status change,
 * cancel (shared flow) and restore. Ported from AppointmentModal.
 */
function SeriesOverviewPanel({ form }) {
  const {
    appointment,
    seriesAppointments,
    treatmentTypes,
    clinicTherapists,
    selectedPatientTherapistIds,
    canReassignSeriesTherapist,
    isAppointmentPastOrFinalized,
    editingOverviewRow,
    overviewEditValues,
    setOverviewEditValues,
    handleOverviewRowEdit,
    cancelOverviewRowEdit,
    handleOverviewRowSave,
    handleSeriesStatusChange,
    handleRestoreAppointment,
    restoringRowId,
    seriesCancelPrompt,
    setSeriesCancelPrompt,
    handleSeriesCancelConfirm,
    pastReassignConfirm,
    setPastReassignConfirm,
    persistOverviewRowUpdate,
    rowScopeEdit,
    recurringConflictsForRows,
    allowConflictsAndResubmit,
    isSubmitting,
  } = form;

  if (!appointment?.isRecurring || !appointment?.recurringGroupId) return null;

  return (
    <div className="apt-wiz-series-overview">
      <div className="apt-wiz-series-overview-header">
        <ListNumbers size={15} weight="bold" />
        <span>
          {seriesAppointments.length > 0
            ? `${seriesAppointments.length} מפגשים בסדרה`
            : "טוען מפגשי סדרה..."}
        </span>
      </div>

      {seriesAppointments.length > 0 && (
        <div className="apt-wiz-series-table-wrap">
          <table className="apt-wiz-series-table">
            <thead>
              <tr>
                <th>#</th>
                <th>תאריך</th>
                <th>יום</th>
                <th>שעה</th>
                {canReassignSeriesTherapist && <th>מטפל/ת</th>}
                {treatmentTypes.length > 0 && <th>סוג טיפול</th>}
                <th>סטטוס</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {seriesAppointments.map((apt) => {
                const aptDate = new Date(apt.startTime);
                const isCurrent = apt.id === appointment?.id;
                const statusLabel =
                  apt.status === "completed"
                    ? "הושלם"
                    : apt.status === "cancelled"
                    ? "בוטל"
                    : "מתוכנן";
                const statusClass =
                  apt.status === "completed"
                    ? "completed"
                    : apt.status === "cancelled"
                    ? "cancelled"
                    : "scheduled";
                const canChangeStatus = apt.status === "scheduled";
                const isEditingRow = editingOverviewRow === apt.id;
                const canEditRow = canChangeStatus || canReassignSeriesTherapist;
                const rowIsPast = isAppointmentPastOrFinalized(apt);
                const aptTherapistName = (() => {
                  if (apt.therapistName) return apt.therapistName;
                  const t = clinicTherapists.find(
                    (ct) => ct.uid === apt.therapistId
                  );
                  return t ? `${t.firstName} ${t.lastName}` : "—";
                })();
                const therapistOptions =
                  selectedPatientTherapistIds.length > 0
                    ? selectedPatientTherapistIds
                    : apt.therapistId
                    ? [apt.therapistId]
                    : [];
                const colCount =
                  4 +
                  (canReassignSeriesTherapist ? 1 : 0) +
                  (treatmentTypes.length > 0 ? 1 : 0) +
                  2;
                return (
                  <Fragment key={apt.id}>
                    {isEditingRow && rowIsPast && (
                      <tr className="apt-wiz-series-warning-row">
                        <td colSpan={colCount}>
                          <div className="apt-wiz-series-warning">
                            <Warning size={13} weight="fill" />
                            <span>
                              שינוי שיוך לטיפול שעבר/הושלם ישפיע על תיעוד
                              היסטורי ועל דוחות. ודא/י שזו אכן הפעולה הרצויה.
                            </span>
                          </div>
                        </td>
                      </tr>
                    )}
                    <tr
                      className={`${isCurrent ? "current" : ""} ${
                        isEditingRow ? "editing" : ""
                      } ${!isEditingRow && rowIsPast ? "past" : ""}`}
                    >
                      <td className="apt-wiz-series-index">
                        {apt.recurringIndex || "—"} / {apt.recurringTotalCount || "—"}
                      </td>
                      {isEditingRow ? (
                        <>
                          <td>
                            <DatePicker
                              value={overviewEditValues.date || ""}
                              onChange={(e) =>
                                setOverviewEditValues((prev) => ({
                                  ...prev,
                                  date: e.target.value,
                                }))
                              }
                            />
                          </td>
                          <td>
                            {overviewEditValues.date
                              ? HEBREW_DAYS_FULL[
                                  new Date(
                                    overviewEditValues.date + "T00:00"
                                  ).getDay()
                                ]
                              : "—"}
                          </td>
                          <td>
                            <TimePicker
                              value={overviewEditValues.time || ""}
                              onChange={(e) =>
                                setOverviewEditValues((prev) => ({
                                  ...prev,
                                  time: e.target.value,
                                }))
                              }
                            />
                          </td>
                          {canReassignSeriesTherapist && (
                            <td>
                              <select
                                value={
                                  overviewEditValues.therapistId ||
                                  apt.therapistId ||
                                  ""
                                }
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const t = clinicTherapists.find(
                                    (ct) => ct.uid === val
                                  );
                                  setOverviewEditValues((prev) => ({
                                    ...prev,
                                    therapistId: val,
                                    therapistName: t
                                      ? `${t.firstName} ${t.lastName}`
                                      : "",
                                  }));
                                }}
                              >
                                {therapistOptions.map((tid) => {
                                  const t = clinicTherapists.find(
                                    (ct) => ct.uid === tid
                                  );
                                  return (
                                    <option key={tid} value={tid}>
                                      {t ? `${t.firstName} ${t.lastName}` : tid}
                                    </option>
                                  );
                                })}
                              </select>
                            </td>
                          )}
                          {treatmentTypes.length > 0 && (
                            <td>
                              <select
                                value={overviewEditValues.treatmentTypeId || ""}
                                onChange={(e) => {
                                  const val = e.target.value;
                                  const tt = treatmentTypes.find(
                                    (t) => t.id === val
                                  );
                                  setOverviewEditValues((prev) => ({
                                    ...prev,
                                    treatmentTypeId: val,
                                    treatmentTypeName: tt ? tt.name : "",
                                  }));
                                }}
                              >
                                <option value="">—</option>
                                {treatmentTypes.map((tt) => (
                                  <option key={tt.id} value={tt.id}>
                                    {tt.name}
                                  </option>
                                ))}
                              </select>
                            </td>
                          )}
                        </>
                      ) : (
                        <>
                          <td>{formatHebrewDate(aptDate)}</td>
                          <td>{HEBREW_DAYS_FULL[aptDate.getDay()]}</td>
                          <td>
                            {aptDate.toLocaleTimeString("he-IL", {
                              hour: "2-digit",
                              minute: "2-digit",
                            })}
                          </td>
                          {canReassignSeriesTherapist && (
                            <td>{aptTherapistName}</td>
                          )}
                          {treatmentTypes.length > 0 && (
                            <td>{apt.treatmentTypeName || "—"}</td>
                          )}
                        </>
                      )}
                      <td>
                        <span className={`apt-wiz-series-status ${statusClass}`}>
                          {statusLabel}
                        </span>
                      </td>
                      <td className="apt-wiz-series-actions">
                        {isEditingRow ? (
                          <>
                            <button
                              type="button"
                              className="apt-wiz-series-btn save"
                              title="שמור"
                              onClick={() => handleOverviewRowSave(apt)}
                            >
                              <Check size={13} weight="bold" />
                            </button>
                            <button
                              type="button"
                              className="apt-wiz-series-btn cancel"
                              title="ביטול"
                              onClick={cancelOverviewRowEdit}
                            >
                              <X size={13} weight="bold" />
                            </button>
                          </>
                        ) : (
                          <>
                            {canEditRow && (
                              <button
                                type="button"
                                className="apt-wiz-series-btn"
                                title={rowIsPast ? "ערוך שיוך מטפל" : "ערוך מפגש"}
                                onClick={() => handleOverviewRowEdit(apt)}
                              >
                                <NotePencil size={13} weight="bold" />
                              </button>
                            )}
                            {canChangeStatus && (
                              <button
                                type="button"
                                className="apt-wiz-series-btn save"
                                title="סמן כהושלם"
                                onClick={() =>
                                  handleSeriesStatusChange(apt.id, "completed")
                                }
                              >
                                <CheckCircle size={13} weight="fill" />
                              </button>
                            )}
                            {canChangeStatus && (
                              <button
                                type="button"
                                className="apt-wiz-series-btn cancel"
                                title="סמן כבוטל"
                                onClick={() =>
                                  handleSeriesStatusChange(apt.id, "cancelled")
                                }
                              >
                                <CalendarX size={13} weight="fill" />
                              </button>
                            )}
                            {apt.status === "cancelled" && (
                              <button
                                type="button"
                                className="apt-wiz-series-btn"
                                title="שחזר מפגש"
                                onClick={() => handleRestoreAppointment(apt)}
                                disabled={restoringRowId === apt.id}
                              >
                                <ArrowsClockwise size={13} weight="bold" />
                              </button>
                            )}
                          </>
                        )}
                      </td>
                    </tr>
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Row-edit conflicts (server refused the reschedule) */}
      {rowScopeEdit && recurringConflictsForRows.length > 0 && (
        <div className="apt-wiz-conflict-panel">
          <div className="apt-wiz-gate-text">
            <Warning size={15} weight="fill" />
            <span>המועדים החדשים מתנגשים עם פגישות קיימות</span>
          </div>
          {!recurringConflictsForRows.some((c) => c.selfConflict) && form.canOverrideTherapistConflict !== false && (
            <button
              type="button"
              className="apt-wiz-gate-btn"
              onClick={allowConflictsAndResubmit}
              disabled={isSubmitting}
            >
              עדכן בכל זאת
            </button>
          )}
          {!recurringConflictsForRows.some((c) => c.selfConflict) && form.canOverrideTherapistConflict === false && (
            <span className="apt-wiz-gate-text apt-wiz-gate-blocked">
              מדיניות הקליניקה חוסמת כפל תורים למטפל/ת. רק מנהל/ת יכול/ה לעקוף.
            </span>
          )}
        </div>
      )}

      {/* Past/finalized row — explicit acknowledgement before rewriting history */}
      <ConfirmDialog
        isOpen={pastReassignConfirm.open}
        onClose={() => setPastReassignConfirm({ open: false, apt: null })}
        onConfirm={() => persistOverviewRowUpdate(pastReassignConfirm.apt)}
        title="עדכון מפגש שהסתיים"
        message="המפגש כבר התקיים או הסתיים. שינוי הפרטים ישפיע על תיעוד היסטורי ועל דוחות. להמשיך?"
        confirmText="עדכן בכל זאת"
        cancelText="ביטול"
        type="warning"
      />

      {/* Lightweight cancel-reason prompt (stale-cache fallback) */}
      <ConfirmDialog
        isOpen={seriesCancelPrompt.open}
        onClose={() =>
          !seriesCancelPrompt.isWorking &&
          setSeriesCancelPrompt({ open: false, aptId: null, reason: "", isWorking: false })
        }
        onConfirm={handleSeriesCancelConfirm}
        title="ביטול מפגש"
        message="לסמן את המפגש כבוטל?"
        confirmText={seriesCancelPrompt.isWorking ? "מבטל..." : "סמן כבוטל"}
        cancelText="חזרה"
        type="warning"
        confirmDisabled={seriesCancelPrompt.isWorking}
      >
        <input
          type="text"
          className="apt-wiz-cancel-reason-input"
          placeholder="סיבת ביטול (אופציונלי)"
          value={seriesCancelPrompt.reason}
          onChange={(e) =>
            setSeriesCancelPrompt((prev) => ({ ...prev, reason: e.target.value }))
          }
          maxLength={300}
        />
      </ConfirmDialog>
    </div>
  );
}

export default SeriesOverviewPanel;
